import { useQuery, useQueryClient } from '@fv/client-core'
import type { ClientAddressLookupResult } from '@fv/client-types'
import type { AddressLookupDTO, CityLookupDTO } from '@fv/models'

import { shipperFetch } from '@/utils/shipperFetch'

import type { AddressBookResponse } from './types'

export type AddressSearch = Omit<AddressLookupDTO, 'skip' | 'limit'>

export const addressSearchKeys = {
  all: ['address-search'] as const,
  search: (args: AddressSearch) => [...addressSearchKeys.all, args] as const,
}

async function searchAddresses(
  dto: AddressSearch,
): Promise<AddressBookResponse> {
  return shipperFetch<AddressBookResponse>(`/addresses`, {
    query: dto,
  })
}

export function useAddressSearch(filter: AddressSearch) {
  return useQuery(
    addressSearchKeys.search(filter),
    async () => await searchAddresses(filter),
    {
      enabled: filter.search.length > 1,
    },
  )
}

export const citySearchKeys = {
  all: ['city-search'] as const,
  search: (args: CityLookupDTO) => [...citySearchKeys.all, args] as const,
}

async function searchCities(
  query: CityLookupDTO,
): Promise<AddressBookResponse> {
  return shipperFetch<AddressBookResponse>(`/addresses/cities`, {
    query,
  })
}

export function useCitySearch(filter: CityLookupDTO) {
  return useQuery(
    citySearchKeys.search(filter),
    async () => await searchCities(filter),
    {
      enabled: filter.city?.trim().length > 1,
    },
  )
}

export function useCacheFormattedAddress() {
  const queryClient = useQueryClient()

  // Not all fields are searchable in Atlas. This helper caches
  // formatted addresses so they're searchable from AddressInputs.
  function cacheFormattedAddress(
    filter: Partial<AddressSearch>,
    location: ClientAddressLookupResult,
  ) {
    const searchOpts: AddressSearch = {
      type: 'shipping',
      includeAddressBook: true,
      includeAtlas: true,
      ...filter,
    }

    // AddressBook & Atlas
    queryClient.setQueryData(addressSearchKeys.search(searchOpts), [location])

    // AddressBook only
    queryClient.setQueryData(
      addressSearchKeys.search({ ...searchOpts, includeAtlas: false }),
      [location],
    )

    // Atlas only
    queryClient.setQueryData(
      addressSearchKeys.search({ ...searchOpts, includeAddressBook: false }),
      [location],
    )
  }

  return cacheFormattedAddress
}
